import { isSafeAiBody, type SafePriceRequest } from '../../supabase/functions/_shared/aiRequest'
import { supabase } from './supabase'
import { DEMO_AI_EXAMPLES, demoNotice } from './aiBudget'
import { kaufwegSichtbar } from './kaufweg'

/**
 * Der EINE Weg, auf dem die Oberfläche die KI erreicht.
 *
 * WARUM ZENTRAL: jede KI-Fläche braucht dieselben fünf Dinge — den Aufruf, den
 * Demomodus, die Fehlercodes der Function in verständliche Sätze übersetzt, den
 * Deckelstand danach, und die Entscheidung, was bei erschöpftem Budget angeboten
 * wird. Baut das jede Fläche selbst, dann sagt die eine „Guthaben aufgebraucht"
 * und die andere „402", und der Nachkauf-Knopf fehlt an zwei von drei Stellen.
 *
 * WARUM DER DEMOMODUS HIER SITZT UND NICHT IN DEN FLÄCHEN: er ist eine
 * Sicherheitsregel, keine Anzeigefrage. Die Demo ist öffentlich zugänglich und hat
 * keinen Mandanten; ein Aufruf von dort ginge ungedeckelt auf den Schlüssel des
 * Betreibers. Die Function lehnt ohne Sitzung ohnehin ab — das ist die eigentliche
 * Sperre —, aber eine Fläche, die es trotzdem versucht, zeigt dem Interessenten
 * einen Fehler statt eines Ergebnisses. Deshalb wird hier gar nicht erst gefragt.
 */

export type AiPurpose = 'enquiry_parse' | 'text_suggestion' | 'price_advice'

export interface AiBudget {
  billed_cent: number
  cap_cent: number
  in_trial: boolean
  paid_cap_cent: number
  overage_cent: number
  resets_at: string
}

/**
 * Was schiefgehen kann — als Fälle, nicht als Zeichenketten. Die Fläche
 * entscheidet damit, ob sie einen Knopf anbietet; ein Text ließe das nicht zu.
 */
export type AiFailure =
  /** Inklusivbetrag erreicht, bezahltes Abo: Nachkauf ist möglich. */
  | { kind: 'exhausted'; billedCent: number; capCent: number; resetsAt: string | null }
  /** Inklusivbetrag erreicht, Testphase: kein Nachkauf, nur Umstieg. */
  | { kind: 'trial_exhausted'; billedCent: number; capCent: number; paidCapCent: number }
  /** Kein Schlüssel hinterlegt — ein Betreiberproblem, kein Kundenproblem. */
  | { kind: 'not_configured' }
  | { kind: 'restricted' }
  /** Bezahlt, aber ohne sichtbaren Text. Siehe `ai_answer_empty` in der Function. */
  | { kind: 'empty' }
  /** Die Sicherheitsprüfung des Modells hat abgelehnt. */
  | { kind: 'refused' }
  /** Lesende Rolle: darf nichts auslösen, das Geld kostet. */
  | { kind: 'forbidden' }
  /** Netz, Server, Unerwartetes. `code` fürs Protokoll, nicht für den Bildschirm. */
  | { kind: 'failed'; code: string }

export type AiResult =
  | { ok: true; text: string; budget: AiBudget | null; demo: boolean; notice: string | null }
  | { ok: false; failure: AiFailure }

export interface AskAiInput {
  purpose: AiPurpose
  prompt?: string
  request?: SafePriceRequest
  system?: string
  maxTokens?: number
  /** `store.live`. Ohne Anmeldung wird das hinterlegte Beispiel geliefert. */
  live: boolean
  de: boolean
}

/**
 * Fragt die KI — oder liefert in der Demo das vorbereitete Beispiel.
 *
 * Wirft nie. Eine KI-Fläche ist immer eine Hilfsfunktion neben der eigentlichen
 * Arbeit; sie darf das Formular, in dem sie steht, nicht mitreißen.
 */
export async function askAi(input: AskAiInput): Promise<AiResult> {
  if (!input.live || !supabase) {
    return {
      ok: true,
      text: DEMO_AI_EXAMPLES[input.purpose] ?? '',
      budget: null,
      demo: true,
      notice: demoNotice(input.de),
    }
  }

  const body = {
    purpose: input.purpose,
    ...(input.request ? { request: input.request } : {}),
    ...(input.prompt !== undefined ? { prompt: input.prompt } : {}),
    ...(input.system !== undefined ? { system: input.system } : {}),
    ...(input.maxTokens !== undefined ? { max_tokens: input.maxTokens } : {}),
  }
  if (!isSafeAiBody(body)) return { ok: false, failure: { kind: 'restricted' } }

  try {
    const { data, error } = await supabase.functions.invoke('ai', {
      body,
    })

    if (error) {
      // Der Fehlercode steckt im Rumpf der Antwort, nicht in der Message. Ohne
      // dieses Auspacken läse der Vermieter „non-2xx status code" — dieselbe
      // Falle wie in QuoteAcceptPanel.
      const res = (error as { context?: Response }).context
      const body = res && typeof res.json === 'function'
        ? await res.json().catch(() => null) as Record<string, unknown> | null
        : null
      return { ok: false, failure: toFailure(body) }
    }

    const d = data as Record<string, unknown>
    return {
      ok: true,
      text: String(d.text ?? ''),
      budget: (d.budget as AiBudget | undefined) ?? null,
      demo: false,
      notice: null,
    }
  } catch (e) {
    console.error('[StaySuite] KI-Aufruf:', String(e))
    return { ok: false, failure: { kind: 'failed', code: 'unreachable' } }
  }
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function toFailure(body: Record<string, unknown> | null): AiFailure {
  const code = String(body?.error ?? 'unknown')
  switch (code) {
    case 'ai_included_exhausted':
      return {
        kind: 'exhausted',
        billedCent: num(body?.billed_cent),
        capCent: num(body?.cap_cent),
        resetsAt: typeof body?.resets_at === 'string' ? body.resets_at : null,
      }
    case 'ai_trial_included_exhausted':
      return {
        kind: 'trial_exhausted',
        billedCent: num(body?.billed_cent),
        capCent: num(body?.cap_cent),
        paidCapCent: num(body?.paid_cap_cent),
      }
    case 'ai_request_not_allowed': return { kind: 'restricted' }
    case 'ai_not_configured': return { kind: 'not_configured' }
    case 'ai_answer_empty': return { kind: 'empty' }
    case 'ai_refused': return { kind: 'refused' }
    case 'forbidden_role': return { kind: 'forbidden' }
    default: return { kind: 'failed', code }
  }
}

/**
 * Der Fehler als Satz — und, wo es einen gibt, der Weg heraus.
 *
 * KEIN FEHLERCODE ERREICHT DEN BILDSCHIRM. Der Vermieter kann mit „422" nichts
 * anfangen; er will wissen, ob er etwas tun kann oder warten muss.
 */
export function aiFailureText(f: AiFailure, de: boolean): {
  title: string
  body: string
  /** Wohin der Knopf führt, falls die Fläche einen anbietet. */
  action?: { label: string; to: string }
} {
  const cent = (n: number) => (n / 100).toFixed(2).replace('.', de ? ',' : '.')

  switch (f.kind) {
    case 'restricted':
      return {
        title: de ? 'KI-Auftrag begrenzt' : 'AI request restricted',
        body: de
          ? 'Freie Gasttexte werden derzeit nicht an die KI übertragen. Bitte erfassen Sie die Anfrage im Formular. Preisvorschläge verwenden ausschließlich ausgewählte Merkmale und Zahlen.'
          : 'Free guest messages are currently not sent to AI. Please enter the enquiry in the form. Price proposals use selected attributes and figures only.',
      }

    case 'exhausted':
      return {
        title: de ? 'Inklusivbetrag für diesen Monat aufgebraucht'
                  : 'Included AI budget used up for this month',
        body: de
          ? `Sie haben ${cent(f.billedCent)} € von ${cent(f.capCent)} € verbraucht. `
            + 'Sie können weitere KI-Leistungen dazubuchen — sie werden mit Ihrer '
            + 'nächsten Rechnung abgerechnet. Ohne Zubuchung steht die KI zu Beginn '
            + 'des nächsten Monats wieder zur Verfügung.'
          : `You have used €${cent(f.billedCent)} of €${cent(f.capCent)}. `
            + 'You can add more AI usage — it is billed with your next invoice. '
            + 'Without it, AI is available again at the start of next month.',
        action: { label: de ? 'KI dazubuchen' : 'Add AI usage', to: '/einstellungen' },
      }

    case 'trial_exhausted':
      return {
        title: de ? 'KI-Betrag der Testphase aufgebraucht'
                  : 'Trial AI budget used up',
        body: de
          ? `In der Testphase sind ${cent(f.capCent)} € KI je Monat enthalten. `
            + `Mit dem bezahlten Abo sind es ${cent(f.paidCapCent)} € je Monat, und `
            + 'darüber hinaus lässt sich dazubuchen.'
          : `The trial includes €${cent(f.capCent)} of AI per month. `
            + `The paid plan includes €${cent(f.paidCapCent)} per month, and beyond `
            + 'that you can add more.',
        // Ohne Kaufweg keine Aufforderung zum Kauf — siehe `lib/kaufweg.ts`.
        // Die Auskunft, dass das Kontingent erschöpft ist, bleibt; nur der Weg
        // zum Abo fehlt, weil es ihn in der Hülle nicht gibt.
        action: kaufwegSichtbar()
          ? { label: de ? 'Abo starten' : 'Start subscription', to: '/billing/setup' }
          : undefined,
      }

    case 'not_configured':
      return {
        title: de ? 'KI steht gerade nicht zur Verfügung' : 'AI is currently unavailable',
        body: de
          ? 'Das liegt nicht an Ihrem Konto. Wir kümmern uns darum; bitte versuchen '
            + 'Sie es später noch einmal.'
          : 'This is not related to your account. We are on it; please try again later.',
      }

    case 'empty':
      return {
        title: de ? 'Keine verwertbare Antwort' : 'No usable answer',
        body: de
          ? 'Die KI hat diesmal nichts Brauchbares geliefert. Bitte noch einmal '
            + 'versuchen — bei einer kürzeren Aufgabe klappt es meist.'
          : 'The AI did not return anything usable this time. Please try again — a '
            + 'shorter task usually works.',
      }

    case 'refused':
      return {
        title: de ? 'Anfrage abgelehnt' : 'Request declined',
        body: de
          ? 'Die KI hat diese Anfrage abgelehnt. Bitte formulieren Sie sie anders.'
          : 'The AI declined this request. Please phrase it differently.',
      }

    case 'forbidden':
      return {
        title: de ? 'Keine Berechtigung' : 'Not permitted',
        body: de
          ? 'Ihre Rolle darf keine KI-Funktionen auslösen. Wenden Sie sich an den '
            + 'Inhaber des Kontos.'
          : 'Your role cannot use AI features. Please ask the account owner.',
      }

    case 'failed':
      return {
        title: de ? 'Das hat nicht geklappt' : 'That did not work',
        body: de
          ? 'Bitte versuchen Sie es noch einmal.'
          : 'Please try again.',
      }
  }
}
